import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

export const RESULTATS_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
export const RESULTATS_TRANSITION = `background-color 180ms ${RESULTATS_EASE}, border-color 180ms ${RESULTATS_EASE}, box-shadow 180ms ${RESULTATS_EASE}, color 160ms ${RESULTATS_EASE}, transform 180ms ${RESULTATS_EASE}`;

export function isDarkMode(theme: Theme): boolean {
  return theme.palette.mode === "dark";
}

/** Accent lisible : orange en dark, primaire en light. */
export function accentColor(theme: Theme): string {
  return isDarkMode(theme)
    ? theme.palette.secondary.light
    : theme.palette.primary.main;
}

export function surfaceSx(theme: Theme, opts?: { emphasize?: boolean }) {
  const dark = isDarkMode(theme);
  return {
    borderRadius: 3,
    border: `1px solid ${
      opts?.emphasize
        ? alpha(theme.palette.secondary.main, dark ? 0.45 : 0.35)
        : theme.palette.divider
    }`,
    bgcolor: theme.palette.background.paper,
    backgroundImage: dark
      ? `linear-gradient(165deg, ${alpha(
          theme.palette.secondary.main,
          0.07
        )} 0%, transparent 42%)`
      : `linear-gradient(165deg, ${alpha(
          theme.palette.primary.main,
          0.05
        )} 0%, transparent 48%)`,
    transition: RESULTATS_TRANSITION,
  } as const;
}

export function interactiveSurfaceSx(theme: Theme) {
  const dark = isDarkMode(theme);
  return {
    ...surfaceSx(theme),
    "&:hover": {
      borderColor: alpha(
        dark ? theme.palette.secondary.light : theme.palette.primary.main,
        dark ? 0.55 : 0.4
      ),
      boxShadow: dark
        ? `0 8px 28px ${alpha(theme.palette.common.black, 0.35)}`
        : `0 10px 28px ${alpha(theme.palette.primary.main, 0.1)}`,
      transform: "translateY(-1px)",
    },
  } as const;
}

export function chipQuietSx(theme: Theme) {
  const dark = isDarkMode(theme);
  return {
    fontWeight: 600,
    color: dark ? theme.palette.common.white : theme.palette.text.primary,
    borderColor: alpha(
      dark ? theme.palette.common.white : theme.palette.primary.main,
      dark ? 0.28 : 0.22
    ),
    bgcolor: dark
      ? alpha(theme.palette.common.white, 0.06)
      : alpha(theme.palette.primary.main, 0.04),
  } as const;
}

/**
 * CTA rempli — en dark, secondary (orange) pour éviter le bleu marine
 * peu contrasté / peu cohérent avec l’UI résultats.
 */
export function ctaContainedSx(theme: Theme) {
  const dark = isDarkMode(theme);
  if (dark) {
    return {
      textTransform: "none" as const,
      fontWeight: 700,
      borderRadius: 2,
      bgcolor: theme.palette.secondary.main,
      color: theme.palette.grey[900],
      "&:hover": {
        bgcolor: theme.palette.secondary.light,
      },
      "&.Mui-disabled": {
        bgcolor: alpha(theme.palette.common.white, 0.1),
        color: alpha(theme.palette.common.white, 0.38),
      },
    };
  }
  return {
    textTransform: "none" as const,
    fontWeight: 700,
    borderRadius: 2,
  };
}

/**
 * CTA outlined — force texte + bordure accent (jamais primary.main sombre).
 */
export function ctaOutlinedSx(theme: Theme) {
  const dark = isDarkMode(theme);
  const accent = accentColor(theme);
  return {
    textTransform: "none" as const,
    fontWeight: 700,
    borderRadius: 2,
    color: accent,
    borderColor: alpha(accent, dark ? 0.55 : 0.45),
    bgcolor: "transparent",
    "&:hover": {
      borderColor: accent,
      bgcolor: alpha(
        dark ? theme.palette.secondary.main : theme.palette.primary.main,
        dark ? 0.14 : 0.06
      ),
      color: accent,
    },
    "&.Mui-disabled": {
      color: alpha(
        dark ? theme.palette.common.white : theme.palette.text.primary,
        0.38
      ),
      borderColor: alpha(
        dark ? theme.palette.common.white : theme.palette.text.primary,
        0.2
      ),
    },
  };
}

/** Lien / bouton texte (Retour, Tour, navigation). */
export function ctaTextSx(theme: Theme) {
  const dark = isDarkMode(theme);
  const accent = accentColor(theme);
  return {
    textTransform: "none" as const,
    fontWeight: 700,
    color: accent,
    "&:hover": {
      bgcolor: alpha(
        dark ? theme.palette.secondary.main : theme.palette.primary.main,
        dark ? 0.14 : 0.06
      ),
      color: accent,
    },
    "&.Mui-disabled": {
      color: alpha(
        dark ? theme.palette.common.white : theme.palette.text.primary,
        0.38
      ),
    },
  };
}

/** Chips de statut outlined — le `default` MUI est trop sombre en dark. */
export function statusChipSx(theme: Theme) {
  const dark = isDarkMode(theme);
  return {
    height: 22,
    fontSize: "0.7rem",
    fontWeight: 700,
    "& .MuiChip-label": { px: 0.75 },
    ...(dark
      ? {
          "&.MuiChip-outlined": {
            borderColor: alpha(theme.palette.common.white, 0.32),
          },
          "&.MuiChip-colorDefault": {
            color: alpha(theme.palette.common.white, 0.9),
            borderColor: alpha(theme.palette.common.white, 0.35),
          },
        }
      : {}),
  } as const;
}
